import { useEffect, useMemo, useState } from "react";

import {
  db,
  auth
} from "./firebase";

import { useClinic } from "./context/ClinicContext";

import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp
} from "./access/firestore";

export default function PacienteForm({
  paciente,
  onClose,
  onSaved
}) {

  const {
    clinicId,
    currentUser
  } = useClinic();

  const [tab, setTab] =
    useState("general");

  const [form, setForm] =
    useState({

      nombre: "",
      apellidos: "",

      telefono: "",

      whatsappCountryCode: "+52",

      whatsapp: "",

      correo: "",

      sexo: "",

      peso: "",

      curp: "",
      rfc: "",

      fechaNacimiento: "",

      calle: "",
      numeroExterior: "",
      numeroInterior: "",

      cp: "",

      colonia: "",
      ciudad: "",
      estado: "",
      pais: "",

      alergias: "",
      antecedentesMedicos: "",
      medicamentos: "",

      contactoEmergencia: "",
      telefonoEmergencia: "",

      observaciones: "",

      // HISTORIA CLÍNICA
      motivoConsulta: "",
      padecimientoActual: "",

      // SISTÉMICOS
      diabetes: false,
      hipertension: false,
      cardiopatias: false,
      asma: false,
      embarazo: false,
      epilepsia: false,
      hepatitis: false,

      cancer: false,
      cancerDetalle: "",

      ets: false,
      etsDetalle: "",

      drogas: false,
      drogasDetalle: "",

      anticonceptivos: false,

      osteoporosis: false,
      anticoagulantes: false,

      otrosPadecimientos: "",

      // HÁBITOS
      tabaco: false,
      tabacoCantidad: "",
      tabacoFrecuencia: "",

      alcohol: false,
      alcoholCantidad: "",
      alcoholFrecuencia: "",

      drogas: false,
      drogasSustancia: "",
      drogasFrecuencia: "",

      bruxismo: false,
      bruxismoEspecificacion: "",

      // DOLOR
      dolor: "",
      escalaDolor: "",

      // SEGURO
      seguroMedico: "",
      numeroPoliza: "",

      // EMERGENCIA
      parentescoEmergencia: "",

      // GENERALES
      estadoCivil: "",
      ocupacion: "",
      nacionalidad: "",

      // SALUD GENERAL
      estadoSaludGeneral: "",

      // COVID
      covidReciente: null,

      // PERIODONCIA / BUCAL
      sangradoEncias: null,
      gingivitis: null,
      enfermedadPeriodontal: null,
      halitosis: null,
      movilidadDental: null,
      dificultadMasticar: null
    });

  const [colonias, setColonias] =
    useState([]);

  const [saving, setSaving] =
    useState(false);

  const whatsappValido = useMemo(() => {

    const numero =
      (form.whatsapp || "")
        .replace(/\D/g, "");

    switch (form.whatsappCountryCode) {

      case "+52": // México
        return numero.length === 10;

      case "+1": // EUA / Canadá
        return numero.length === 10;

      case "+57": // Colombia
        return numero.length === 10;

      case "+54": // Argentina
        return numero.length >= 10 &&
              numero.length <= 11;

      case "+56": // Chile
        return numero.length === 9;

      case "+51": // Perú
        return numero.length === 9;

      case "+34": // España
        return numero.length === 9;

      default:
        return numero.length >= 8 &&
              numero.length <= 15;

    }

  }, [
    form.whatsapp,
    form.whatsappCountryCode
  ]);

  const getWhatsAppPrefix = (countryCode) => {

    switch (countryCode) {

      case "+52":
        return "521";

      case "+1":
        return "1";

      case "+57":
        return "57";

      case "+54":
        return "54";

      case "+56":
        return "56";

      case "+51":
        return "51";

      case "+34":
        return "34";

      default:
        return (
          countryCode || ""
        ).replace("+", "");

    }

  };

  // =========================
  // LOAD
  // =========================

  useEffect(() => {

    if (!paciente) return;

    let whatsapp =
      (paciente.whatsapp || "").replace(/\D/g, "");

    const prefix =
      getWhatsAppPrefix(
        paciente.whatsappCountryCode || "+52"
      );

    if (
      whatsapp.startsWith(prefix)
    ) {
      whatsapp =
        whatsapp.substring(prefix.length);
    }

    setForm(prev => ({

      ...prev,

      ...paciente,

      whatsapp,

      whatsappCountryCode:
        paciente.whatsappCountryCode || "+52"

    }));

  }, [paciente]);

  // =========================
  // EDAD
  // =========================

  const edad = useMemo(() => {

    if (!form.fechaNacimiento)
      return "";

    const hoy =
      new Date();

    const nacimiento =
      new Date(
        form.fechaNacimiento
      );

    let edad =
      hoy.getFullYear() -
      nacimiento.getFullYear();

    const m =
      hoy.getMonth() -
      nacimiento.getMonth();

    if (
      m < 0 ||
      (
        m === 0 &&
        hoy.getDate() <
        nacimiento.getDate()
      )
    ) {
      edad--;
    }

    return edad;

  }, [form.fechaNacimiento]);

  // =========================
  // BUSCAR CP
  // =========================

  const buscarCP =
    async (cp) => {

      if (
        !cp ||
        cp.length !== 5
      ) return;

      try {

        const res =
          await fetch(
            `https://api.zippopotam.us/mx/${cp}`
          );

        if (!res.ok) return;

        const data =
          await res.json();

        const places =
          data.places || [];

        const listaColonias =
          places.map(
            p => p["place name"]
          );

        setColonias(
          listaColonias
        );

        setForm(prev => ({

          ...prev,

          colonia:
            prev.colonia ||
            listaColonias[0] ||
            "",

          ciudad:
            prev.ciudad || "",

          estado:
            places[0]?.state ||
            "",

          pais: "México"

        }));

      } catch (err) {

        console.log(
          "Error CP:",
          err
        );
      }
    };

  useEffect(() => {

    if (
      form.cp &&
      form.cp.length === 5
    ) {

      buscarCP(form.cp);
    }

  }, [form.cp]);

  // =========================
  // GUARDAR
  // =========================

  const guardar = async () => {

    if (
      !form.nombre ||
      !form.apellidos
    ) {

      alert(
        "Nombre y apellidos obligatorios"
      );

      return;
    }

    if (!clinicId) {

      alert("No se encontró la clínica");

      return;
    }

    setSaving(true);

    try {

      const numeroLimpio =
        (form.whatsapp || "")
          .replace(/\D/g, "");

      const whatsappNormalizado =
        `${getWhatsAppPrefix(form.whatsappCountryCode)}${numeroLimpio}`;

      const payload = {

        ...form,

        whatsapp: whatsappNormalizado,

        updatedAt:
          serverTimestamp(),

        updatedBy:
          auth.currentUser?.uid || "",

        updatedByName:
          currentUser?.nombre || "",

        updatedByEmail:
          auth.currentUser?.email || ""

      };

      if (paciente?.id) {

        await updateDoc(
          doc(
            db,
            "clinics",
            clinicId,
            "pacientes",
            paciente.id
          ),
          payload
        );

        onSaved?.({
          ...payload,
          id: paciente.id
        });

      } else {

        const ref =
          await addDoc(
            collection(
              db,
              "clinics",
              clinicId,
              "pacientes"
            ),
            {
              ...payload,

              createdAt:
                serverTimestamp(),

              createdBy:
                auth.currentUser?.uid || "",

              createdByName:
                currentUser?.nombre || "",

              createdByEmail:
                auth.currentUser?.email || ""
            }
          );

        onSaved?.({
          ...payload,
          id: ref.id
        });
      }

      onClose();

    } catch (err) {

      console.error(err);

      alert(
        "Error guardando paciente"
      );

    } finally {

      setSaving(false);
    }
  };

  // =========================
  // STYLES
  // =========================

  const isMobile =
      window.innerWidth <= 1023;

  const container = {
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.28)",
      display: "flex",
      justifyContent: "center",
      alignItems: isMobile ? "flex-start" : "center",
      zIndex: 9999999,
      padding: isMobile
          ? "calc(116px + env(safe-area-inset-top)) 12px calc(92px + env(safe-area-inset-bottom))"
          : 20,
      boxSizing: "border-box"
  };

  const modal = {
      width: isMobile ? "100%" : 980,
      maxWidth: isMobile ? "100%" : "95vw",
      height: isMobile
          ? "calc(100dvh - 116px - 92px - env(safe-area-inset-top) - env(safe-area-inset-bottom))"
          : "auto",
      maxHeight: isMobile ? "none" : "92vh",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      background: "#ffffff",
      borderRadius: isMobile ? 24 : 28,
      padding: isMobile
          ? "20px 16px 24px"
          : 28,
      display: "flex",
      flexDirection: "column",
      gap: isMobile ? 16 : 22,
      boxShadow: isMobile
          ? "0 12px 35px rgba(15,23,42,0.16)"
          : "0 25px 80px rgba(0,0,0,0.25)",
      fontFamily: "Inter, system-ui",
      boxSizing: "border-box"
  };

  const section = {
      background: "#f8fafc",
      borderRadius: isMobile ? 18 : 20,
      padding: isMobile ? 16 : 18,
      display: "flex",
      flexDirection: "column",
      gap: isMobile ? 16 : 14,
      border: "1px solid #e5e7eb",
      boxSizing: "border-box"
  };

  const sectionTitle = {
      fontSize: isMobile ? 17 : 14,
      fontWeight: 700,
      color: "#111827",
      marginBottom: 2
  };

  const input = {
      width: "100%",
      minWidth: 0,
      padding: isMobile
          ? "13px 14px"
          : "10px 12px",
      borderRadius: isMobile ? 14 : 12,
      border: "1px solid #d1d5db",
      outline: "none",
      fontSize: isMobile ? 16 : 13,
      background: "#ffffff",
      color: "#1e293b",
      boxSizing: "border-box"
  };

  const dateInput = {
      ...input,
      WebkitAppearance: "none",
      appearance: "none",
      display: "block",
      width: "100%",
      minWidth: 0,
      height: isMobile ? 50 : "auto",
      lineHeight: isMobile ? "normal" : undefined,
      boxSizing: "border-box"
  };

  const textarea = {
      ...input,
      minHeight: isMobile ? 110 : 90,
      resize: "vertical"
  };

  const row = (
      label,
      element
  ) => (
      <div
          style={{
              display: "grid",
              gridTemplateColumns: isMobile
                  ? "1fr"
                  : "140px 1fr",
              gap: isMobile ? 7 : 10,
              alignItems: "center"
          }}
      >
          <div
              style={{
                  fontWeight: 600,
                  fontSize: isMobile ? 14 : 13,
                  color: "#475569"
              }}
          >
              {label}
          </div>

          {element}
      </div>
  );

  const tabButton = (
      active
  ) => ({
      flex: isMobile ? "1 1 calc(50% - 5px)" : "initial",
      minHeight: isMobile ? 46 : "auto",
      padding: isMobile
          ? "11px 12px"
          : "10px 16px",
      borderRadius: isMobile ? 14 : 12,
      border: active
          ? "1px solid #2563eb"
          : "1px solid #e2e8f0",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: isMobile ? 14 : 13,
      background: active
          ? "#2563eb"
          : "#f1f5f9",
      color: active
          ? "#ffffff"
          : "#334155",
      boxSizing: "border-box"
  });

  // =========================
  // RENDER
  // =========================

  return (

    <div style={container}>

      <div style={modal}>

        {/* HEADER */}

        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: isMobile ? "sticky" : "static",
          top: 0,
          zIndex: 20,
          margin: isMobile ? "-20px -16px 0" : 0,
          padding: isMobile
            ? "14px 16px 14px"
            : 0,
          background: "#ffffff",
          borderBottom: isMobile
            ? "1px solid #e2e8f0"
            : "none",
          boxSizing: "border-box"
        }}>

          <div style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 12 : 16,
            minWidth: 0
          }}>

            <div style={{
              width: isMobile ? 46 : 70,
              height: isMobile ? 46 : 70,
              minWidth: isMobile ? 46 : 70,
              borderRadius: "50%",
              background: "#eff6ff",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: isMobile ? 20 : 28
            }}>
              
            </div>

            <div>

              <div style={{
                fontSize: isMobile ? 20 : 26,
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1.2
              }}>

                {paciente
                  ? "Editar paciente"
                  : "Nuevo paciente"}

              </div>

             <div style={{
              color: "#64748b",
              marginTop: 4,
              fontSize: isMobile ? 12 : 14,
              lineHeight: 1.3
            }}>
                Expediente clínico odontológico
              </div>

            </div>

          </div>

          <button
            onClick={onClose}
            style={{
                border: "none",
                background: "#f1f5f9",
                color: "#475569",
                width: 42,
                height: 42,
                minWidth: 42,
                borderRadius: 14,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}
          >
            ✕
          </button>

        </div>

        {/* TABS */}

        <div style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap"
        }}>

          <button
            onClick={() =>
              setTab("general")
            }
            style={tabButton(
              tab === "general"
            )}
          >
            General
          </button>

          <button
            onClick={() =>
              setTab("direccion")
            }
            style={tabButton(
              tab === "direccion"
            )}
          >
            Dirección
          </button>

          <button
            onClick={() =>
              setTab("clinico")
            }
            style={tabButton(
              tab === "clinico"
            )}
          >
            Clínico
          </button>

          <button
            onClick={() =>
              setTab("extras")
            }
            style={tabButton(
              tab === "extras"
            )}
          >
            Extras
          </button>

        </div>

        {/* GENERAL */}

        {tab === "general" && (

          <div style={section}>

            <div style={sectionTitle}>
              Información general
            </div>

            {row(
              "Nombre",
              <input
                style={input}
                value={form.nombre}
                onChange={e =>
                  setForm({
                    ...form,
                    nombre:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "Apellidos",
              <input
                style={input}
                value={form.apellidos}
                onChange={e =>
                  setForm({
                    ...form,
                    apellidos:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "Sexo",
              <select
                style={input}
                value={form.sexo}
                onChange={e =>
                  setForm({
                    ...form,
                    sexo:
                      e.target.value
                  })
                }
              >
                <option value="">
                  Seleccionar
                </option>

                <option>
                  Masculino
                </option>

                <option>
                  Femenino
                </option>
              </select>
            )}

            {row(
              "Estado civil",
              <select
                style={input}
                value={form.estadoCivil}
                onChange={e =>
                  setForm({
                    ...form,
                    estadoCivil:
                      e.target.value
                  })
                }
              >
                <option value="">
                  Seleccionar
                </option>

                <option>
                  Soltero(a)
                </option>

                <option>
                  Casado(a)
                </option>

                <option>
                  Unión libre
                </option>

                <option>
                  Divorciado(a)
                </option>

                <option>
                  Viudo(a)
                </option>
              </select>
            )}

            {row(
              "Nacimiento",
              <input
                type="date"
                style={dateInput}
                value={
                  form.fechaNacimiento
                }
                onChange={e =>
                  setForm({
                    ...form,
                    fechaNacimiento:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "Edad",
              <div style={{
                fontWeight: 700
              }}>
                {edad
                  ? `${edad} años`
                  : "—"}
              </div>
            )}

            {row(
              "Peso (kg)",
              <input
                type="number"
                step="0.1"
                style={input}
                value={form.peso}
                onChange={e =>
                  setForm({
                    ...form,
                    peso:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "Teléfono de Casa",
              <input
                style={input}
                value={form.telefono}
                onChange={e =>
                  setForm({
                    ...form,
                    telefono:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "WhatsApp",

              <div style={{
                display:"flex",
                gap:10
              }}>

                <select
                  style={{
                    ...input,
                    width:140
                  }}
                  value={
                    form.whatsappCountryCode ||
                    "+52"
                  }
                  onChange={e => {

                    const countryCode =
                      e.target.value;

                    let prefix = "";

                    switch (countryCode) {

                      case "+52":
                        prefix = "521";
                        break;

                      case "+1":
                        prefix = "1";
                        break;

                      case "+57":
                        prefix = "57";
                        break;

                      case "+54":
                        prefix = "54";
                        break;

                      case "+56":
                        prefix = "56";
                        break;

                      case "+51":
                        prefix = "51";
                        break;

                      case "+34":
                        prefix = "34";
                        break;

                      default:
                        prefix =
                          countryCode.replace("+", "");

                    }

                    setForm({

                      ...form,

                      whatsappCountryCode:
                        countryCode

                    });

                  }}
                >

                  <option value="+52">
                    🇲🇽 México (+52)
                  </option>

                  <option value="+1">
                    🇺🇸 Estados Unidos (+1)
                  </option>

                  <option value="+1">
                    🇨🇦 Canadá (+1)
                  </option>

                  <option value="+57">
                    🇨🇴 Colombia (+57)
                  </option>

                  <option value="+54">
                    🇦🇷 Argentina (+54)
                  </option>

                  <option value="+56">
                    🇨🇱 Chile (+56)
                  </option>

                  <option value="+51">
                    🇵🇪 Perú (+51)
                  </option>

                  <option value="+34">
                    🇪🇸 España (+34)
                  </option>

                </select>

                <input
                  value={getWhatsAppPrefix(form.whatsappCountryCode)}
                  readOnly
                  style={{
                    ...input,

                    width: 70,

                    textAlign: "center",

                    background: "#f3f4f6",

                    fontWeight: 700,

                    color: "#374151",

                    cursor: "default"
                  }}
                />

                <input
                  style={{
                    ...input,

                    border:
                      form.whatsapp.length === 0
                        ? "1px solid #d1d5db"
                        : whatsappValido
                          ? "2px solid #22c55e"
                          : "2px solid #ef4444",

                    background:
                      form.whatsapp.length === 0
                        ? "#fff"
                        : whatsappValido
                          ? "#f0fdf4"
                          : "#fef2f2"
                  }}
                  value={form.whatsapp}
                  onChange={e =>
                    setForm({
                      ...form,
                      whatsapp:
                        e.target.value
                          .replace(/\D/g,"")
                    })
                  }
                  placeholder="Número"
                />

              </div>
            )}

            {row(
              "Correo",
              <input
                style={input}
                value={form.correo}
                onChange={e =>
                  setForm({
                    ...form,
                    correo:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "CURP",
              <input
                style={input}
                value={form.curp}
                onChange={e =>
                  setForm({
                    ...form,
                    curp:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "RFC",
              <input
                style={input}
                value={form.rfc}
                onChange={e =>
                  setForm({
                    ...form,
                    rfc:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "Ocupación",
              <input
                style={input}
                value={form.ocupacion}
                onChange={e =>
                  setForm({
                    ...form,
                    ocupacion:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "Nacionalidad",
              <input
                style={input}
                value={form.nacionalidad}
                onChange={e =>
                  setForm({
                    ...form,
                    nacionalidad:
                      e.target.value
                  })
                }
              />
            )}

          </div>

        )}

        {/* DIRECCIÓN */}

        {tab === "direccion" && (

          <div style={section}>

            <div style={sectionTitle}>
              Dirección
            </div>

            {row(
              "Calle",
              <input
                style={input}
                value={form.calle}
                onChange={e =>
                  setForm({
                    ...form,
                    calle:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "No. exterior",
              <input
                style={input}
                value={
                  form.numeroExterior
                }
                onChange={e =>
                  setForm({
                    ...form,
                    numeroExterior:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "No. interior",
              <input
                style={input}
                value={
                  form.numeroInterior
                }
                onChange={e =>
                  setForm({
                    ...form,
                    numeroInterior:
                      e.target.value
                  })
                }
              />
            )}

            {row(
              "Código postal",
              <input
                style={input}
                value={form.cp}
                onChange={e => {

                  const cp =
                    e.target.value
                      .replace(
                        /\D/g,
                        ""
                      );

                  setForm({
                    ...form,
                    cp
                  });
                }}
              />
            )}

            {row(
              "Colonia",
              <select
                style={input}
                value={
                  form.colonia
                }
                onChange={e =>
                  setForm({
                    ...form,
                    colonia:
                      e.target.value
                  })
                }
              >

                <option value="">
                  Seleccionar
                </option>

                {colonias.map(
                  (c, i) => (

                    <option
                      key={i}
                    >
                      {c}
                    </option>

                  )
                )}

              </select>
            )}

            {row(
              "Ciudad",
              <input
                  style={input}
                  value={form.ciudad}
                  onChange={e =>
                      setForm({
                          ...form,
                          ciudad:
                              e.target.value
                      })
                  }
              />
            )}

            {row(
              "Estado",
              <input
                style={input}
                value={
                  form.estado
                }
                readOnly
              />
            )}

            {row(
              "País",
              <input
                style={input}
                value={
                  form.pais
                }
                readOnly
              />
            )}

          </div>

        )}

        {/* CLÍNICO */}

        {tab === "clinico" && (

          <div style={{
            display:"flex",
            flexDirection:"column",
            gap:18
          }}>

            {/* HISTORIA */}
            <div style={section}>

              <div style={sectionTitle}>
                Historia clínica
              </div>

              {row(
                "Motivo de Consulta",
                <input
                  style={input}
                  value={
                    form.motivoConsulta
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      motivoConsulta:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Padecimiento Actual",
                <textarea
                  style={textarea}
                  value={
                    form.padecimientoActual
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      padecimientoActual:
                        e.target.value
                    })
                  }
                />
              )}

            </div>

            {/* DOLOR */}
            <div style={section}>

              <div style={sectionTitle}>
                Dolor
              </div>

              {row(
                "Descripción",
                <input
                  style={input}
                  value={form.dolor}
                  onChange={e =>
                    setForm({
                      ...form,
                      dolor:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Escala",
                <input
                  type="number"
                  min="1"
                  max="10"
                  style={input}
                  value={
                    form.escalaDolor
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      escalaDolor:
                        e.target.value
                    })
                  }
                />
              )}

            </div>

            {/* INFO CLÍNICA */}
            <div style={section}>

              <div style={sectionTitle}>
                Información Médica
              </div>

              {row(
                "Salud general",
                <select
                  style={input}
                  value={
                    form.estadoSaludGeneral
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      estadoSaludGeneral:
                        e.target.value
                    })
                  }
                >
                  <option value="">
                    Seleccionar
                  </option>

                  <option>
                    Buena
                  </option>

                  <option>
                    Regular
                  </option>

                  <option>
                    Mala
                  </option>
                </select>
              )}

              {row(
                "COVID reciente",
                <div style={{
                  display:"flex",
                  gap:18
                }}>

                  <label style={{
                    display:"flex",
                    alignItems:"center",
                    gap:6
                  }}>

                    <input
                      type="checkbox"
                      checked={
                        form.covidReciente === true
                      }
                      onChange={() =>
                        setForm({
                          ...form,
                          covidReciente: true
                        })
                      }
                    />

                    Sí

                  </label>

                  <label style={{
                    display:"flex",
                    alignItems:"center",
                    gap:6
                  }}>

                    <input
                      type="checkbox"
                      checked={
                        form.covidReciente === false
                      }
                      onChange={() =>
                        setForm({
                          ...form,
                          covidReciente: false
                        })
                      }
                    />

                    No

                  </label>

                </div>
              )}

              {row(
                "Alergias",
                <textarea
                  style={textarea}
                  value={form.alergias}
                  onChange={e =>
                    setForm({
                      ...form,
                      alergias:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Antecedentes Médicos",
                <textarea
                  style={textarea}
                  value={
                    form
                      .antecedentesMedicos
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      antecedentesMedicos:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Medicamentos Actuales",
                <textarea
                  style={textarea}
                  value={
                    form.medicamentos
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      medicamentos:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Cirugías",
                <div
                  style={{
                    display:"flex",
                    flexDirection:"column",
                    gap:10,
                    width:"100%"
                  }}
                >

                  <label
                    style={{
                      display:"flex",
                      alignItems:"center",
                      gap:8
                    }}
                  >

                    <input
                      type="checkbox"
                      checked={
                        form.cirugias || false
                      }
                      onChange={e =>
                        setForm({
                          ...form,
                          cirugias:
                            e.target.checked,
                          cirugiasDetalle:
                            e.target.checked
                              ? form.cirugiasDetalle || ""
                              : ""
                        })
                      }
                    />

                    Sí

                  </label>

                  {form.cirugias && (

                    <textarea
                      style={textarea}
                      placeholder="Especifique las cirugías realizadas"
                      value={
                        form.cirugiasDetalle || ""
                      }
                      onChange={e =>
                        setForm({
                          ...form,
                          cirugiasDetalle:
                            e.target.value
                        })
                      }
                    />

                  )}

                </div>
              )}

              {row(
                "Transfusiones sanguíneas",
                <div
                  style={{
                    display:"flex",
                    flexDirection:"column",
                    gap:10,
                    width:"100%"
                  }}
                >

                  <label
                    style={{
                      display:"flex",
                      alignItems:"center",
                      gap:8
                    }}
                  >

                    <input
                      type="checkbox"
                      checked={
                        form.transfusiones || false
                      }
                      onChange={e =>
                        setForm({
                          ...form,
                          transfusiones:
                            e.target.checked,
                          transfusionesDetalle:
                            e.target.checked
                              ? form.transfusionesDetalle || ""
                              : ""
                        })
                      }
                    />

                    Sí

                  </label>

                  {form.transfusiones && (

                    <textarea
                      style={textarea}
                      placeholder="Especifique las transfusiones"
                      value={
                        form.transfusionesDetalle || ""
                      }
                      onChange={e =>
                        setForm({
                          ...form,
                          transfusionesDetalle:
                            e.target.value
                        })
                      }
                    />

                  )}

                </div>
              )}

            </div>

            {/* SISTÉMICOS */}
            <div style={section}>

              <div style={sectionTitle}>
                Enfermedades sistémicas
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12
                }}
              >

                {[
                  ["Diabetes", "diabetes"],
                  ["Hipertensión", "hipertension"],
                  ["Cardiopatías", "cardiopatias"],
                  ["Asma", "asma"],
                  ["Embarazo", "embarazo"],
                  ["Epilepsia", "epilepsia"],
                  ["Hepatitis", "hepatitis"],
                  ["Cáncer", "cancer"],
                  ["Enfermedades de Transmisión Sexual", "ets"],
                  ["Anticonceptivos", "anticonceptivos"],
                  ["Osteoporosis", "osteoporosis"],
                  ["Anticoagulantes", "anticoagulantes"]
                ].map(([label, key]) => (

                  <label
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 500
                    }}
                  >

                    <input
                      type="checkbox"
                      checked={form[key] || false}
                      onChange={e => {

                        const checked = e.target.checked;

                        setForm({

                          ...form,

                          [key]: checked,

                          ...(key === "cancer" && !checked
                            ? { cancerDetalle: "" }
                            : {}),

                          ...(key === "ets" && !checked
                            ? { etsDetalle: "" }
                            : {}),

                          ...(key === "drogas" && !checked
                            ? { drogasDetalle: "" }
                            : {})

                        });

                      }}
                    />

                    {label}

                  </label>

                ))}

              </div>

              {form.cancer &&

                row(
                  "Especifique cáncer",
                  <input
                    style={input}
                    placeholder="Ej. Cáncer de mama"
                    value={form.cancerDetalle || ""}
                    onChange={e =>
                      setForm({
                        ...form,
                        cancerDetalle: e.target.value
                      })
                    }
                  />
                )

              }

              {form.ets &&

                row(
                  "Especifique ETS",
                  <input
                    style={input}
                    placeholder="Ej. VPH, VIH, Sífilis..."
                    value={form.etsDetalle || ""}
                    onChange={e =>
                      setForm({
                        ...form,
                        etsDetalle: e.target.value
                      })
                    }
                  />
                )

              }

              {row(
                "Otros",
                <textarea
                  style={textarea}
                  value={form.otrosPadecimientos}
                  onChange={e =>
                    setForm({
                      ...form,
                      otrosPadecimientos: e.target.value
                    })
                  }
                />
              )}

            </div>

            {/* BUCAL */}

            <div style={section}>

              <div style={sectionTitle}>
                Estado bucal
              </div>

              {[
                [
                  "Sangrado gingival",
                  "sangradoEncias"
                ],

                [
                  "Gingivitis",
                  "gingivitis"
                ],

                [
                  "Enfermedad periodontal",
                  "enfermedadPeriodontal"
                ],

                [
                  "Halitosis",
                  "halitosis"
                ],

                [
                  "Movilidad dental",
                  "movilidadDental"
                ],

                [
                  "Dificultad para masticar",
                  "dificultadMasticar"
                ]

              ].map(([label,key]) => (

                <div
                  key={key}
                  style={{
                    display:"grid",
                    gridTemplateColumns:
                      "220px 1fr",
                    alignItems:"center",
                    gap:10
                  }}
                >

                  <div style={{
                    fontWeight:600,
                    fontSize:13
                  }}>
                    {label}
                  </div>

                  <div style={{
                    display:"flex",
                    gap:18
                  }}>

                    <label style={{
                      display:"flex",
                      alignItems:"center",
                      gap:6
                    }}>

                      <input
                        type="checkbox"
                        checked={
                          form[key] === true
                        }
                        onChange={() =>
                          setForm({
                            ...form,
                            [key]: true
                          })
                        }
                      />

                      Sí

                    </label>

                    <label style={{
                      display:"flex",
                      alignItems:"center",
                      gap:6
                    }}>

                      <input
                        type="checkbox"
                        checked={
                          form[key] === false
                        }
                        onChange={() =>
                          setForm({
                            ...form,
                            [key]: false
                          })
                        }
                      />

                      No

                    </label>

                  </div>

                </div>

              ))}

            </div>

            {/* HÁBITOS */}
            <div style={section}>

              <div style={sectionTitle}>
                Hábitos
              </div>

              {/* TABACO */}

              <div>

                <label
                  style={{
                    display:"flex",
                    alignItems:"center",
                    gap:8,
                    fontWeight:500
                  }}
                >

                  <input
                    type="checkbox"
                    checked={form.tabaco || false}
                    onChange={e=>{

                      const checked=e.target.checked;

                      setForm({

                        ...form,

                        tabaco:checked,

                        ...(checked ? {} : {

                          tabacoCantidad:"",
                          tabacoFrecuencia:""

                        })

                      });

                    }}
                  />

                  Tabaco

                </label>

                {form.tabaco &&

                  <div
                    style={{
                      display:"flex",
                      gap:12,
                      marginTop:10,
                      marginLeft:28
                    }}
                  >

                    <input
                      style={{
                        ...input,
                        width:180
                      }}
                      placeholder="Cantidad"
                      value={form.tabacoCantidad || ""}
                      onChange={e=>
                        setForm({
                          ...form,
                          tabacoCantidad:e.target.value
                        })
                      }
                    />

                    <input
                      style={{
                        ...input,
                        width:220
                      }}
                      placeholder="Frecuencia"
                      value={form.tabacoFrecuencia || ""}
                      onChange={e=>
                        setForm({
                          ...form,
                          tabacoFrecuencia:e.target.value
                        })
                      }
                    />

                  </div>

                }

              </div>

              {/* ALCOHOL */}

              <div>

                <label
                  style={{
                    display:"flex",
                    alignItems:"center",
                    gap:8,
                    fontWeight:500,
                    marginTop:18
                  }}
                >

                  <input
                    type="checkbox"
                    checked={form.alcohol || false}
                    onChange={e=>{

                      const checked=e.target.checked;

                      setForm({

                        ...form,

                        alcohol:checked,

                        ...(checked ? {} : {

                          alcoholCantidad:"",
                          alcoholFrecuencia:""

                        })

                      });

                    }}
                  />

                  Alcohol

                </label>

                {form.alcohol &&

                  <div
                    style={{
                      display:"flex",
                      gap:12,
                      marginTop:10,
                      marginLeft:28
                    }}
                  >

                    <input
                      style={{
                        ...input,
                        width:180
                      }}
                      placeholder="Cantidad"
                      value={form.alcoholCantidad || ""}
                      onChange={e=>
                        setForm({
                          ...form,
                          alcoholCantidad:e.target.value
                        })
                      }
                    />

                    <input
                      style={{
                        ...input,
                        width:220
                      }}
                      placeholder="Frecuencia"
                      value={form.alcoholFrecuencia || ""}
                      onChange={e=>
                        setForm({
                          ...form,
                          alcoholFrecuencia:e.target.value
                        })
                      }
                    />

                  </div>

                }

              </div>

              {/* DROGAS */}

              <div>

                <label
                  style={{
                    display:"flex",
                    alignItems:"center",
                    gap:8,
                    fontWeight:500,
                    marginTop:18
                  }}
                >

                  <input
                    type="checkbox"
                    checked={form.drogas || false}
                    onChange={e=>{

                      const checked=e.target.checked;

                      setForm({

                        ...form,

                        drogas:checked,

                        ...(checked ? {} : {

                          drogasSustancia:"",
                          drogasFrecuencia:""

                        })

                      });

                    }}
                  />

                  Drogas

                </label>

                {form.drogas &&

                  <div
                    style={{
                      display:"flex",
                      gap:12,
                      marginTop:10,
                      marginLeft:28
                    }}
                  >

                    <input
                      style={{
                        ...input,
                        width:220
                      }}
                      placeholder="Sustancia"
                      value={form.drogasSustancia || ""}
                      onChange={e=>
                        setForm({
                          ...form,
                          drogasSustancia:e.target.value
                        })
                      }
                    />

                    <input
                      style={{
                        ...input,
                        width:220
                      }}
                      placeholder="Frecuencia"
                      value={form.drogasFrecuencia || ""}
                      onChange={e=>
                        setForm({
                          ...form,
                          drogasFrecuencia:e.target.value
                        })
                      }
                    />

                  </div>

                }

              </div>

              {/* BRUXISMO */}

              <div>

                <label
                  style={{
                    display:"flex",
                    alignItems:"center",
                    gap:8,
                    fontWeight:500,
                    marginTop:18
                  }}
                >

                  <input
                    type="checkbox"
                    checked={form.bruxismo || false}
                    onChange={e=>
                      setForm({
                        ...form,
                        bruxismo:e.target.checked
                      })
                    }
                  />

                  Bruxismo

                </label>

                {form.bruxismo &&

                  <div
                    style={{
                      marginTop:10,
                      marginLeft:28
                    }}
                  >

                    <input
                      style={input}
                      placeholder="Especificar"
                      value={form.bruxismoEspecificacion || ""}
                      onChange={e=>
                        setForm({
                          ...form,
                          bruxismoEspecificacion:e.target.value
                        })
                      }
                    />

                  </div>

                }

              </div>

            </div>

          </div>

        )}

        {/* EXTRAS */}

        {tab === "extras" && (

          <div style={{
            display:"flex",
            flexDirection:"column",
            gap:18
          }}>

            {/* SEGURO */}
            <div style={section}>

              <div style={sectionTitle}>
                Seguro médico
              </div>

              {row(
                "Seguro",
                <input
                  style={input}
                  value={
                    form.seguroMedico
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      seguroMedico:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Póliza",
                <input
                  style={input}
                  value={
                    form.numeroPoliza
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      numeroPoliza:
                        e.target.value
                    })
                  }
                />
              )}

            </div>

            {/* EMERGENCIA */}
            <div style={section}>

              <div style={sectionTitle}>
                Contacto de emergencia
              </div>

              {row(
                "Nombre",
                <input
                  style={input}
                  value={
                    form.contactoEmergencia
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      contactoEmergencia:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Teléfono",
                <input
                  style={input}
                  value={
                    form.telefonoEmergencia
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      telefonoEmergencia:
                        e.target.value
                    })
                  }
                />
              )}

              {row(
                "Parentesco",
                <input
                  style={input}
                  value={
                    form.parentescoEmergencia
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      parentescoEmergencia:
                        e.target.value
                    })
                  }
                />
              )}

            </div>

            {/* OBSERVACIONES */}
            <div style={section}>

              <div style={sectionTitle}>
                Observaciones
              </div>

              <textarea
                style={textarea}
                value={
                  form.observaciones
                }
                onChange={e =>
                  setForm({
                    ...form,
                    observaciones:
                      e.target.value
                  })
                }
              />

            </div>

          </div>

        )}

        {/* FOOTER */}

        <div style={{
          display: "flex",
          justifyContent:
            "space-between",
          marginTop: 8
        }}>

          <button
            onClick={onClose}
            style={{
              padding:
                "10px 18px",
              borderRadius: 12,
              border: "none",
              background: "#e5e7eb",
              cursor: "pointer",
              fontWeight: 600
            }}
          >
            Cancelar
          </button>

          <button
            onClick={guardar}
            disabled={saving}
            style={{
              padding:
                "10px 20px",
              borderRadius: 12,
              border: "none",
              background:
                saving
                  ? "#94a3b8"
                  : "#2563eb",
              color: "#fff",
              cursor:
                saving
                  ? "not-allowed"
                  : "pointer",
              fontWeight: 700
            }}
          >
            {saving
              ? "Guardando..."
              : "Guardar paciente"}
          </button>

        </div>

      </div>

    </div>
  );
}